// Renovation Cost Estimator – Flanders
// Combines the stored reference values (from the C-REAL pilot data preparation) with the
// user's input. The estimator itself performs no price adjustment or statistics.

import RENOVATION_REFERENCE_FLANDERS from "./renovationReferenceFlanders.json";

export { RENOVATION_REFERENCE_FLANDERS };

// ---------------------------------------------------------------------------
// Fixed rules and assumptions of the method
// ---------------------------------------------------------------------------

// Flemish EPC scale (kWh/m² per year): A ≤ 100, B ≤ 200, C ≤ 300, D ≤ 400, E ≤ 500, F > 500
export const EPC_LABELS = ["A", "B", "C", "D", "E", "F"];
export const CURRENT_LABELS = ["B", "C", "D", "E", "F"];

// Target label → upper limit of its band (the value the house must at least reach)
export const TARGET_LABEL_VALUE = { A: 100, B: 200, C: 300, D: 400, E: 500 };

export const BUILDING_TYPES = ["detached", "semi-detached", "terraced"];
export const EXPOSED_SIDES = { detached: 4, "semi-detached": 3, terraced: 2 };
export const STOREYS = [1, 2, 3]; // 3 = "3 or more"

const STOREY_HEIGHT = 2.8; // m
const ROOF_FACTOR = 1.2; // pitched roof
const WINDOW_SHARE = 0.15; // of floor area

export const ENERGY_MEASURES = ["roof", "windows", "walls", "floor", "heating", "solar"];
export const NON_ENERGY_WORKS = ["electrical", "sanitary", "other"];
const SIZE_BASED_MEASURES = ["roof", "windows", "walls", "floor"];

const FOOTPRINT_WARNING_BELOW = 40; // m²
const FLOOR_AREA_RANGE = { min: 107, max: 415 }; // m², range of the pilot houses

// Labels better than the given current label
export function targetLabelsFor(currentLabel) {
  return EPC_LABELS.slice(0, EPC_LABELS.indexOf(currentLabel)).filter(
    (label) => label in TARGET_LABEL_VALUE
  );
}

export function hasReferenceValues(reference = RENOVATION_REFERENCE_FLANDERS) {
  const filled = (row) => row && [row.low, row.typical, row.high].every(Number.isFinite);
  return (
    CURRENT_LABELS.every((label) => filled(reference.part1[label])) &&
    [...ENERGY_MEASURES, ...NON_ENERGY_WORKS].every((m) => filled(reference.part2[m]))
  );
}

const scaleRange = (row, factor) => ({
  low: row.low * factor,
  typical: row.typical * factor,
  high: row.high * factor,
});

const sumRanges = (rows) =>
  rows.reduce(
    (sum, row) => ({
      low: sum.low + row.low,
      typical: sum.typical + row.typical,
      high: sum.high + row.high,
    }),
    { low: 0, typical: 0, high: 0 }
  );

// ---------------------------------------------------------------------------
// Element sizes (square footprint)
// ---------------------------------------------------------------------------
export function elementSizes({ floorArea, storeys, buildingType }) {
  const footprint = floorArea / storeys;
  const windows = WINDOW_SHARE * floorArea;
  const perimeter = 4 * Math.sqrt(footprint);
  const walls = Math.max(
    perimeter * STOREY_HEIGHT * storeys * (EXPOSED_SIDES[buildingType] / 4) - windows,
    0
  );
  return { footprint, roof: footprint * ROOF_FACTOR, floor: footprint, windows, walls };
}

function heatingFactor(floorArea) {
  if (floorArea <= 120) return 0.9;
  if (floorArea <= 200) return 1.0;
  return 1.15;
}

// ---------------------------------------------------------------------------
// Part 1 – budget to reach a target EPC label
// ---------------------------------------------------------------------------
export function estimateBudgetToTarget({
  currentLabel,
  targetLabel,
  floorArea,
  reference = RENOVATION_REFERENCE_FLANDERS,
}) {
  const area = Number(floorArea);
  const row = reference.part1[currentLabel];
  if (!(area > 0) || !row || !targetLabelsFor(currentLabel).includes(targetLabel)) return null;

  const currentValue = row.representativeEpc;
  const targetValue = TARGET_LABEL_VALUE[targetLabel];
  const epcImprovement = currentValue - targetValue;

  return {
    currentLabel,
    targetLabel,
    currentValue,
    targetValue,
    epcImprovement,
    budget: scaleRange(row, epcImprovement * area),
    lessReliable: Boolean(row.lessReliable),
  };
}

// ---------------------------------------------------------------------------
// Part 2 – indicative cost per measure
// ---------------------------------------------------------------------------
export function estimateCostPerMeasure({
  floorArea,
  buildingType,
  storeys,
  measures = [],
  nonEnergyWorks = [],
  reference = RENOVATION_REFERENCE_FLANDERS,
}) {
  const area = Number(floorArea);
  const storeyCount = Number(storeys);
  if (!(area > 0) || measures.length + nonEnergyWorks.length === 0) return null;

  const needsSizes = measures.some((m) => SIZE_BASED_MEASURES.includes(m));
  if (needsSizes && (!EXPOSED_SIDES[buildingType] || !(storeyCount >= 1))) return null;

  const sizes = needsSizes
    ? elementSizes({ floorArea: area, storeys: storeyCount, buildingType })
    : null;

  const energy = ENERGY_MEASURES.filter((m) => measures.includes(m)).map((measure) => {
    const row = reference.part2[measure];
    if (SIZE_BASED_MEASURES.includes(measure)) {
      return { measure, size: sizes[measure], ...scaleRange(row, sizes[measure]) };
    }
    if (measure === "heating") return { measure, ...scaleRange(row, heatingFactor(area)) };
    return { measure, ...scaleRange(row, 1) }; // solar panels
  });

  const nonEnergy = NON_ENERGY_WORKS.filter((w) => nonEnergyWorks.includes(w)).map((work) => ({
    measure: work,
    ...scaleRange(reference.part2[work], 1),
  }));

  const energyTotal = sumRanges(energy);
  const nonEnergyTotal = sumRanges(nonEnergy);

  return {
    energy,
    nonEnergy,
    energyTotal,
    nonEnergyTotal,
    total: sumRanges([energyTotal, nonEnergyTotal]),
    footprint: sizes?.footprint ?? area / (storeyCount || 1),
    smallFootprint:
      measures.some((m) => ["roof", "floor", "walls"].includes(m)) &&
      sizes.footprint < FOOTPRINT_WARNING_BELOW,
  };
}

export function isFloorAreaOutsidePilotRange(floorArea) {
  const area = Number(floorArea);
  return area > 0 && (area < FLOOR_AREA_RANGE.min || area > FLOOR_AREA_RANGE.max);
}
